import { FileRejection } from "@mantine/dropzone";
import { useState } from "react";
import { ONCE_TIME_UPLOAD_MAX_FILES } from "@/configs/constants/app/voc-data-management";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { VoCFileUploadAPIRequest } from "@/services/vocFiles/vocFiles.api.types";
import { VoCFilesService } from "@/services/vocFiles/vocFiles.service";
import uploadFileByPresignedURL from "@/utils/uploadFileByPresignedURL";

const useFileUpload = () => {
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [remainingFiles, setRemainingFiles] = useState(
    ONCE_TIME_UPLOAD_MAX_FILES,
  );
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [fileStatus, setFileStatus] = useState<{
    [key: number]: "loading" | "success" | "error";
  }>({});
  const [isAllFilesUploaded, setIsAllFilesUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // DropzoneのAcceptedFiles、handleRejectedFilesを処理
  // NOTE: 一度にDropすると、AcceptedFilesとRejectedFilesが同時に発火する
  const handleAcceptedFiles = (files: File[]) => {
    if (files.length > remainingFiles) {
      const accepted = files.slice(0, remainingFiles);
      setAcceptedFiles((prevFiles) => [...prevFiles, ...accepted]);
      setRemainingFiles(
        (prevRemainingFiles) => prevRemainingFiles - accepted.length,
      );
      setNewErrorMessage(
        "アップロード可能なファイル数を超えたため、一部のファイルを削除しました。",
      );
    } else {
      setAcceptedFiles((prevFiles) => [...prevFiles, ...files]);
      setRemainingFiles(
        (prevRemainingFiles) => prevRemainingFiles - files.length,
      );
    }
  };
  const handleRejectedFiles = (_files: FileRejection[]) => {
    // TODO: Rejectedされたファイルに関する表示をする場合、ここに処理を追加する
    // NOTE: 今はエラーメッセージのみ表示する
    setNewErrorMessage(
      "アップロード不可のファイルが選択されたため、削除しました。",
    );
  };

  // エラーアラートを追加、削除
  const setNewErrorMessage = (message: string) => {
    setErrorMessages((prevErrorMessages) => [...prevErrorMessages, message]);
  };
  const clearErrorMessage = (index: number) => {
    setErrorMessages((prevErrorMessages) => prevErrorMessages.filter((_, i) => i !== index));
  };

  // 選択されたファイルを削除
  const handleDeleteSelectedFile = (index: number) => {
    setAcceptedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setRemainingFiles((prevRemainingFiles) => prevRemainingFiles + 1);
    setFileStatus((prevStatus) => {
      const newStatus = { ...prevStatus };
      delete newStatus[index];
      return newStatus;
    });
  };

  // 決まったファイル数をアップロード
  const handleUpload = async () => {
    setIsUploading(true);
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      const request_data: VoCFileUploadAPIRequest = {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        workspaceId: sessionCurrentWorkspaceId,
      };

      setFileStatus((prevStatus) => ({ ...prevStatus, [i]: "loading" }));

      try {
        const presignedUrl = await VoCFilesService.postVoCFileAndGetPresignedURL(request_data);

        const response = await uploadFileByPresignedURL(file, presignedUrl);

        if (response) {
          setFileStatus((prevStatus) => ({ ...prevStatus, [i]: "success" }));
        } else {
          setFileStatus((prevStatus) => ({ ...prevStatus, [i]: "error" }));
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        setFileStatus((prevStatus) => ({ ...prevStatus, [i]: "error" }));
        continue;
      }
    }
    setIsUploading(false);
    setIsAllFilesUploaded(true);
  };

  // 続けてアップロードするために、アップロード済みのファイルをリセット
  const handleResetUploaded = () => {
    setAcceptedFiles([]);
    setRemainingFiles(ONCE_TIME_UPLOAD_MAX_FILES);
    setFileStatus({});
    setIsAllFilesUploaded(false);
  };

  return {
    acceptedFiles,
    remainingFiles,
    errorMessages,
    fileStatus,
    isAllFilesUploaded,
    isUploading,
    handleAcceptedFiles,
    handleRejectedFiles,
    handleDeleteSelectedFile,
    handleUpload,
    handleResetUploaded,
    clearErrorMessage,
  };
};

export default useFileUpload;
